import { createSlice } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';

// onChange is handed straight to UI components that call their callback with extra positional
// args of their own — PixelSelector with (value, selectedOption, removed), PixelMultiChecks with
// (list, clickedValue). Those would land in `wire` and get saved instead of the value, so a wire
// is only honoured when it is actually a delta envelope.
// ponytail: shape check, not a flag — every wire in this form is { add, remove }.
export const isDelta = (wire) => !!wire && typeof wire === 'object' && !Array.isArray(wire) && ('add' in wire || 'remove' in wire);

// a delta the store can merge: uid-keyed rows in, uids out. An attachments delta (files in, ids out)
// is not one — it only ever travels up, and its answer is the server's whole list
export const isRowDelta = (value) => isDelta(value)
    && (value.add || []).every((row) => !!row?.uid)
    && (value.remove || []).every((uid) => typeof uid === 'string');

const initialState = {
    reportId: null,
    reportNumber: null,
    edit: false,
    creating: false,       // true while the report row is being created (incident just chosen, before reportId)
    activeStep: null,      // current stepper tab id (= section name) → drives lazy section loading
    syncing: false,        // derived: true while a field is saving → locks the tabs
    syncStatus: 'idle',    // 'idle' | 'saving' | 'saved' | 'error'
    syncMessage: null,     // error text when syncStatus === 'error'
    lastSyncedAt: null,    // timestamp of the last successful save
    lastUpdatedBy: null,   // name of the remote collaborator behind the last update (null = it was me)
    onlineUsers: [],       // presence members on report.{id}  ({ id, name })
    connection: 'online',  // realtime socket: 'online' | 'reconnecting' | 'offline' (meaningful only while subscribed)
    loadedSections: [],    // sections whose data has been fetched (acts as a set) → cleared on disconnect
    resyncing: false,      // true while the open section re-hydrates after a reconnect
    retryingLoads: 0,      // >0 while a dependent-section fetch is waiting on its next retry
    incident: {},          // driver: { incident_id, incident_category, investigation_technique, incident_title }
    values: {},            // flat: { 'section.field': value }  (value = scalar OR array)
    dirtyFields: {},       // { 'section.field': true } — fields with an un-synced local edit (guards them from stale echoes)
    heldDeltas: {},        // { 'section.field': [delta, ...] } — echoes that arrived before their section loaded
};

const pruneCause = (values, uid) => {
    ['cause_description', 'cause_evidence', 'attachments'].forEach((field) => {
        delete values[`causes.${field}.${uid}`];
    });
    Object.entries(values).forEach(([key, value]) => {
        if (key.startsWith('actions.causes.') && Array.isArray(value) && value.includes(uid)) {
            values[key] = value.filter((v) => v !== uid);
        }
    });
};
const pruneBarrier = (values, uid) => {
    ['barrier_description', 'barrier_evidence', 'attachments'].forEach((field) => {
        delete values[`barriers.${field}.${uid}`];
    });
};
// a delta echo ({ add, remove }) merges into the list, never replacing it, so two clients' writes
// land in any order: `remove` drops a row and everything keyed under its uid, `add` upserts in
// place (a renamed free cause comes back as an add). A catalog cause the server already holds
// under another client's uid replaces the local row for it — the server keeps one row per cause,
// and that row is the one every other client sees.
const mergeRows = (values, key, delta) => {
    const current = Array.isArray(values[key]) ? values[key] : [];
    const add = delta?.add || [];
    const remove = new Set(delta?.remove || []);
    const isCausesKey = key === 'causes.causes';
    const isBarriersKey = key === 'barriers.barriers';

    add.forEach((row) => {
        if (isCausesKey && row.cause_id != null) {
            current.forEach((existing) => {
                if (existing.uid !== row.uid && existing.cause_id != null && +existing.cause_id === +row.cause_id) remove.add(existing.uid);
            });
        }
        if (isBarriersKey && row.barrier_id != null) {
            current.forEach((existing) => {
                if (existing.uid !== row.uid && existing.barrier_id != null && +existing.barrier_id === +row.barrier_id) remove.add(existing.uid);
            });
        }
    });

    current.filter((row) => remove.has(row.uid)).forEach((row) => {
        if (isCausesKey) pruneCause(values, row.uid);
        if (isBarriersKey) pruneBarrier(values, row.uid);
    });

    const addByUid = new Map(add.map((row) => [row.uid, row]));
    const kept = current.filter((row) => !remove.has(row.uid)).map((row) => addByUid.get(row.uid) || row);
    const keptUids = new Set(kept.map((row) => row.uid));

    return [...kept, ...add.filter((row) => !keptUids.has(row.uid))];
};
const reportSlice = createSlice({
    name: 'report',
    initialState,
    reducers: {
        initReport: (state, action) => ({
            ...initialState,
            reportId: action.payload?.reportId ?? null,
            reportNumber: action.payload?.reportNumber ?? null,
            edit: !!action.payload?.edit,
        }),
        setReportIdentity: (state, action) => {
            state.reportId = action.payload?.id ?? null;
            state.reportNumber = action.payload?.report_number ?? null;
            state.creating = false; // report now exists
        },
        setCreating: (state, action) => {
            state.creating = !!action.payload;
        },
        setActiveStep: (state, action) => {
            state.activeStep = action.payload;
        },
        setIncident: (state, action) => {
            state.incident = { ...state.incident, ...action.payload };
        },
        setValue: (state, action) => {
            state.values[action.payload.key] = action.payload.value;
        },
        // unchecking a cause: drop its Cause Description keys and pull it out of every
        // actions group membership list (mirrors the backend prune, so re-checking can't resurrect)
        pruneCauseValues: (state, action) => {
            pruneCause(state.values, action.payload);
        },
        pruneBarrierValues: (state, action) => {
            pruneBarrier(state.values, action.payload);
        },
        // removing a details row: drop every per-row key (section.<field>.<uid>) it left behind
        pruneRowValues: (state, action) => {
            const { section, uid } = action.payload;
            const suffix = `.${uid}`;
            Object.keys(state.values).forEach((key) => {
                if (key.startsWith(`${section}.`) && key.endsWith(suffix)) delete state.values[key];
            });
        },
        // mark/clear a field as having an un-synced local edit → guards it against stale realtime echoes
        markDirty: (state, action) => {
            state.dirtyFields[action.payload] = true;
        },
        clearDirty: (state, action) => {
            delete state.dirtyFields[action.payload];
        },
        // apply an incoming realtime field echo without clobbering a field the user is mid-editing:
        // skip while it holds an un-synced local edit, skip no-ops, else apply + credit the remote editor.
        applyRemoteValue: (state, action) => {
            const { key, value, option = null, name = null, at = null } = action.payload || {};
            // a delta merges even while dirty — a local row the user just ticked is not among what
            // the delta names, and the write carrying that row is still on its way
            if (state.dirtyFields[key] && !isRowDelta(value)) return;
            // ...but not before its section has loaded: the GET it races may have read the rows before
            // this write landed, so it waits and merges once the section is in (addLoadedSection).
            // Seeding a list from it instead would pass for the loaded membership, and the tabs that
            // gate their fetch on the value existing would never load the real one
            if (isRowDelta(value) && !state.loadedSections.includes(key.split('.')[0])) {
                state.heldDeltas[key] = [...(state.heldDeltas[key] || []), value];
                return;
            }
            // the echo carries the picked option's {id,name} → seed it so a selector can label a value
            // it never fetched (its own list stays untouched; the seed is per field / per row)
            if (option) {
                const [section, ...field] = key.split('.');
                const optionsKey = `${section}.selectorOptions`;
                state.values[optionsKey] = { ...(state.values[optionsKey] || {}), [field.join('.')]: option };
            }
            const next = isRowDelta(value) ? mergeRows(state.values, key, value) : value;
            if (isEqual(state.values[key], next)) return;
            state.values[key] = next;
            // a remote per-row field for a row we don't track yet → add it to the section's membership
            // list so the row renders live (actions/interviews/timeLines → <section>.<section>, injuries/pur → <section>.details).
            // injuries/pur only address their repeater through `detail_` fields — their other row-addressed
            // fields (the injuries totals, keyed by injury type) are not rows and must not seed a phantom one.
            const rowMatch = key.match(/^(actions|injuries|pur|interviews|timeLines)\.([^.]+)\.(.+)$/);
            if (rowMatch) {
                const [, sec, field, uid] = rowMatch;
                const ownRepeater = ['actions', 'interviews', 'timeLines'].includes(sec);
                const listKey = ownRepeater ? `${sec}.${sec}` : `${sec}.details`;
                const list    = Array.isArray(state.values[listKey]) ? state.values[listKey] : [];
                if ((ownRepeater || field.startsWith('detail_')) && !list.some((r) => r.uid === uid)) {
                    state.values[listKey] = [...list, { uid }];
                }
            }
            if (at) state.lastSyncedAt = at;
            state.lastUpdatedBy = name || null;
        },
        hydrate: (state, action) => {
            state.values = { ...state.values, ...action.payload };
        },
        // sync lifecycle: { status, message?, at? } → also keeps `syncing` + `lastSyncedAt` in sync
        setSyncStatus: (state, action) => {
            const { status, message = null, at = null } = action.payload || {};
            state.syncStatus = status;
            state.syncMessage = message;
            state.syncing = status === 'saving';
            if (status === 'saved' && at) {
                state.lastSyncedAt = at;
                state.lastUpdatedBy = null; // my own save → no "by"
            }
        },
        // a remote collaborator changed a field → reflect who/when in the "last updated" line
        setRemoteUpdate: (state, action) => {
            const { name = null, at = null } = action.payload || {};
            if (at) state.lastSyncedAt = at;
            state.lastUpdatedBy = name || null;
        },
        // presence
        setOnlineUsers: (state, action) => {
            state.onlineUsers = Array.isArray(action.payload) ? action.payload : [];
        },
        addOnlineUser: (state, action) => {
            const user = action.payload;
            if (user && !state.onlineUsers.some((m) => m.id === user.id)) {
                state.onlineUsers.push(user);
            }
        },
        removeOnlineUser: (state, action) => {
            const user = action.payload;
            state.onlineUsers = state.onlineUsers.filter((m) => m.id !== user?.id);
        },
        // realtime connection + resync
        setConnection: (state, action) => {
            state.connection = action.payload;
        },
        addLoadedSection: (state, action) => {
            if (!state.loadedSections.includes(action.payload)) state.loadedSections.push(action.payload);
            Object.keys(state.heldDeltas).filter((key) => key.startsWith(`${action.payload}.`)).forEach((key) => {
                state.heldDeltas[key].forEach((delta) => { state.values[key] = mergeRows(state.values, key, delta); });
                delete state.heldDeltas[key];
            });
        },
        clearLoadedSections: (state) => {
            state.loadedSections = [];
        },
        setResyncing: (state, action) => {
            state.resyncing = !!action.payload;
        },
        setRetryingLoad: (state, action) => {
            state.retryingLoads = Math.max(0, state.retryingLoads + (action.payload ? 1 : -1));
        },
        // queue rather than overwrite: a delta body only carries what that one write changed, so an
        // earlier failure for the same field is not redundant — dropping it would drop that change
        resetReport: () => initialState,
    },
});

export const {
    initReport,
    setReportIdentity,
    setCreating,
    setActiveStep,
    setIncident,
    setValue,
    pruneCauseValues,
    pruneBarrierValues,
    pruneRowValues,
    markDirty,
    clearDirty,
    applyRemoteValue,
    hydrate,
    setSyncStatus,
    setRemoteUpdate,
    setOnlineUsers,
    addOnlineUser,
    removeOnlineUser,
    setConnection,
    addLoadedSection,
    clearLoadedSections,
    setResyncing,
    setRetryingLoad,
    resetReport,
} = reportSlice.actions;

export default reportSlice.reducer;
